import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Zap, ListChecks } from 'lucide-react';

import { cn } from '@shared/utils/cn';
import { Skeleton } from '@shared/components/ui/skeleton';
import { Progress } from '@shared/components/ui/progress';
import { StatusLabel } from '@shared/components/status-label';
import { EmptyContent } from '@shared/components/empty-content';
import { WarehouseBreadcrumbs } from '@shared/components/breadcrumbs';

import { SessionStatusChip } from '@features/customization-session/components/session-status-chip';
import { useGetSessionsQuery } from '@features/customization-session/api/customization-session-api';
import {
  TechniqueFilter,
  matchesTechnique,
  useTechniqueFilter,
  getTechniqueOptions,
  getTechniqueDotClass,
  resolveTechniqueFilter,
} from '@features/customization-technique';

const PAGE_TITLE = 'Sessions de personnalisation';
const REFRESH_INTERVAL_MS = 120_000;

/** Sessions with something left to do (a session whose products are all put back is no longer listed). */
export const SessionsView = () => {
  const navigate = useNavigate();
  const {
    data: sessions,
    isLoading,
    isError,
  } = useGetSessionsQuery(undefined, { pollingInterval: REFRESH_INTERVAL_MS, skipPollingIfUnfocused: true });
  const [storedFilter, setFilter] = useTechniqueFilter();

  const techniques = useMemo(() => getTechniqueOptions(sessions ?? []), [sessions]);
  const filter = resolveTechniqueFilter(storedFilter, techniques);
  const filtered = useMemo(
    () => (sessions ?? []).filter((session) => matchesTechnique(session, filter)),
    [sessions, filter]
  );

  return (
    <div className="mx-auto flex w-full max-w-400 flex-col gap-6">
      <WarehouseBreadcrumbs
        breadcrumbs={[{ name: 'Atelier', href: '/workshop' }, { name: PAGE_TITLE }]}
        page={PAGE_TITLE}
      />

      {techniques.length > 0 && (
        <TechniqueFilter onChange={setFilter} options={techniques} unit="session" value={filter} />
      )}

      {isLoading && <Skeleton className="h-40 rounded-lg" />}

      {isError && (
        <EmptyContent
          description="La liste n'a pas pu être chargée."
          icon={<ListChecks />}
          title="Chargement impossible"
        />
      )}

      {sessions?.length === 0 && (
        <EmptyContent
          description="Créez une session depuis le plan de l'atelier, en choisissant une famille de produits."
          icon={<ListChecks />}
          title="Aucune session en cours"
        />
      )}

      <ul
        aria-label="Sessions de personnalisation"
        className="grid gap-4"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))' }}
      >
        {filtered.map((session) => {
          const tracked = session.status === 'to_load' || session.status === 'customized';
          const done = session.status === 'customized' ? session.stored : session.loaded;

          return (
            <li className="flex" key={session.id}>
              <button
                className="flex h-full w-full flex-col gap-3 rounded-xl border border-l-4 border-border bg-card p-4 text-left text-card-foreground transition-colors outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-primary"
                onClick={() => navigate(`/workshop/sessions/${session.id}`)}
                style={{ borderLeftColor: session.machineColor }}
                type="button"
              >
                <div className="flex items-center gap-2">
                  <span
                    aria-hidden
                    className={cn('size-2.5 shrink-0 rounded-full', getTechniqueDotClass(session.technique.color))}
                    title={session.technique.name}
                  />
                  <span className="min-w-0 flex-1 truncate text-base font-semibold" title={session.family}>
                    {session.family}
                  </span>
                  {session.express && (
                    <StatusLabel color="error" startIcon={<Zap />} variant="soft">
                      Express
                    </StatusLabel>
                  )}
                </div>

                <span className="text-sm text-muted-foreground">
                  N°{session.id} · {session.machine} · gabarit {session.jig}
                </span>

                <div>
                  <SessionStatusChip status={session.status} />
                </div>

                <div className="mt-auto flex flex-col gap-1">
                  <div className="flex justify-between">
                    <span className="text-xs text-muted-foreground">
                      {session.quantity} produit{session.quantity > 1 ? 's' : ''}
                    </span>
                    {tracked && (
                      <span className="text-xs tabular-nums">
                        {done} / {session.quantity} {session.status === 'to_load' ? 'chargés' : 'rangés'}
                      </span>
                    )}
                  </div>
                  {/* Reserve the height of the bar so cards stay aligned whatever the status. */}
                  <Progress
                    className={
                      tracked
                        ? session.status === 'to_load'
                          ? 'h-1.5 bg-warning/24 [&>div]:bg-warning'
                          : 'h-1.5 bg-success/24 [&>div]:bg-success'
                        : 'invisible h-1.5'
                    }
                    value={(done / session.quantity) * 100}
                  />
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
